import React, { useEffect } from "react";
import { Tooltip } from "react-tooltip";
import styled from "styled-components";
import {
  FlexColumn,
  StyledLabelText,
  StyledH4,
  StyledBodyText,
  FlexRow,
  StyledH0,
  StyledBodyTextSmall,
  Digits,
} from "../../styles";
import Icon from "../common/Icon";
import infoIcon from "../../assets/icons/info.png";
import { formatValueToShort, outcomeDataMapper } from "./utils";

type CardPaddingVariant = "default" | "compactTop";

type ContainerProps = {
  type: "small" | "large";
  $paddingVariant: CardPaddingVariant;
};

const getCardPadding = ({ type, $paddingVariant }: ContainerProps) => {
  if (type === "large") return "8px 8px 16px";
  if ($paddingVariant === "compactTop") return "8px 8px 16px";

  return "16px 8px 16px";
};

const Container = styled(FlexColumn)<ContainerProps>`
  width: ${(props) => (props.type === "large" ? "376px" : "280px")};
  height: 320px;
  border-radius: 16px;
  background: ${(props) =>
    props.type === "large"
      ? "linear-gradient(184.52deg, #a9c1d5 -69.37%, #d9e8fb 96.33%)"
      : "#f7f7f7"};
  padding: ${getCardPadding};
`;

const IconContainer = styled(FlexRow)`
  align-content: center;
  justify-content: center;
`;

const IconContainerLarge = styled(FlexColumn)`
  justify-content: center;
  align-items: center;
  min-height: 0;

  img {
    min-height: 0;
  }
`;

const CardHeader = styled(FlexRow)`
  align-content: start;
  height: 10%;
  min-height: 30px;
  margin-bottom: 8px;
  justify-content: space-between;
`;

const VerifiedEstimatedContainer = styled(FlexRow)`
  min-width: 100%;
  padding-left: 28px;
  padding-right: 28px;
  justify-content: space-between;
`;

const VerifiedEstimatedContainerLarge = styled(FlexRow)`
  min-width: 100%;
  padding-left: 16px;
  padding-right: 16px;
  justify-content: space-between;
`;

const VerifiedContainer = styled(FlexColumn)`
  padding-left: 8px;
  border-left: 2px solid #14cd8d;
`;

const EstimatedContainer = styled(FlexColumn)`
  padding-left: 8px;
  border-left: 2px solid #05291d;
  align-self: end;
`;

const Volume = styled(Digits)`
  padding-top: 8px;
`;

const VolumeLarge = styled(StyledH0)`
  padding-top: 16px;
`;

const TopImpactTagContainer = styled(FlexRow)`
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  border-radius: 18px;
`;

const TopImpactText = styled(StyledBodyTextSmall)`
  padding: 6px 16px;
`;

const VolumeDetailed = styled(StyledBodyText)`
  color: #5a5a5a;
`;

const Description = styled(StyledBodyText)`
  padding-bottom: 16px;
  color: #5a5a5a;
  text-align: center;
`;

const DescriptionLarge = styled(StyledBodyText)`
  padding-bottom: 16px;
  text-align: center;
`;

const CardContent = styled(FlexColumn)`
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
`;

const DataContainer = styled(FlexColumn)`
  height: 60%;
  align-items: center;
  justify-content: center;
`;

const DataContainerLarge = styled(FlexColumn)`
  align-items: center;
  justify-content: end;
`;

const StyledTooltip = styled(Tooltip)`
  && {
    background-color: white;
    color: black;
    border-radius: 16px;
    border: 1px #c2c9ce solid;
    font-size: 12px;
    width: 200px;
`;
const InfoHeader = styled(FlexRow)`
  margin-bottom: 8px;
  justify-content: end;
`;
const StyledInfoIcon = styled.img`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

function TopImpactTag() {
  return (
    <TopImpactTagContainer>
      <TopImpactText>Top Impact</TopImpactText>
    </TopImpactTagContainer>
  );
}

type Props = {
  volume: number;
  unit: string;
  verified: number;
  estimated: number;
  type: "small" | "large";
  isSingleImpact: boolean;
  isVerifiedEstimatedFeatureEnabled: boolean;
  needsM2Icon?: boolean;
};

function ImpactCard({
  volume,
  unit,
  verified,
  estimated,
  type,
  isSingleImpact,
  isVerifiedEstimatedFeatureEnabled,
  needsM2Icon = false,
}: Props) {
  const [cardData, setCardData] = React.useState(outcomeDataMapper(unit));

  const unitWithTooltip =
    unit === "Biodiversity" || (unit === "m2" && needsM2Icon);
  const paddingVariant: CardPaddingVariant = unitWithTooltip
    ? "compactTop"
    : "default";
  const biodiversityTooltip =
    "Earthly's voluntary biodiversity credits (VBC) each represent a 3x3m area of land within a wider project where actions are being taken to conserve and restore biodiversity.";
  const m2Tooltip = `This value shows the land area supported by your investment. For projects assessed from April 2026, it is based on annual issuance, updated with each monitoring report, and reflects the land supported in the investment year.`;

  const tooltipContent =
    unit === "Biodiversity" ? biodiversityTooltip : m2Tooltip;

  useEffect(() => {
    setCardData(outcomeDataMapper(unit));
  }, [unit, type]);

  if (type === "large") {
    return (
      <Container
        type={type}
        $paddingVariant={paddingVariant}
        data-testid={`impact-card-${type}-${unit}`}
      >
        <CardHeader>
          {!isSingleImpact && <TopImpactTag />}
          {unitWithTooltip && (
            <StyledInfoIcon
              src={infoIcon}
              alt="Icon"
              data-tooltip-id={`infoicon-${unit}`}
            />
          )}
        </CardHeader>
        <CardContent>
          <IconContainerLarge>
            <Icon
              size="large"
              icon={cardData?.icon}
              description={cardData?.unit}
            />
          </IconContainerLarge>
          <DataContainerLarge>
            <VolumeLarge>
              {volume > 999999
                ? `${formatValueToShort(volume)}*`
                : volume.toLocaleString()}
            </VolumeLarge>
            {volume > 999999 && (
              <VolumeDetailed>{`*${volume.toLocaleString()}`}</VolumeDetailed>
            )}
            <DescriptionLarge data-tooltip-id={`infoicon-${unit}`}>
              {cardData?.description}
            </DescriptionLarge>
            {isVerifiedEstimatedFeatureEnabled && (
              <VerifiedEstimatedContainerLarge>
                <VerifiedContainer>
                  <StyledLabelText>Verified</StyledLabelText>
                  <StyledH4>{`${verified}%`}</StyledH4>
                </VerifiedContainer>
                <EstimatedContainer>
                  <StyledLabelText>Estimated</StyledLabelText>
                  <StyledH4>{`${estimated}%`}</StyledH4>
                </EstimatedContainer>
              </VerifiedEstimatedContainerLarge>
            )}
          </DataContainerLarge>
        </CardContent>
        {unitWithTooltip && (
          <StyledTooltip
            id={`infoicon-${unit}`}
            noArrow
            place="bottom-end"
            content={tooltipContent}
            opacity={1}
          />
        )}
      </Container>
    );
  }

  return (
    <Container
      type={type}
      $paddingVariant={paddingVariant}
      data-testid={`impact-card-${type}-${unit}`}
    >
      <InfoHeader>
        {unitWithTooltip && (
          <StyledInfoIcon
            src={infoIcon}
            alt="Icon"
            data-tooltip-id={`infoicon-${unit}`}
          />
        )}
      </InfoHeader>
      <IconContainer>
        <Icon icon={cardData?.icon} description={cardData?.unit} />
      </IconContainer>
      <DataContainer>
        <Volume>{volume.toLocaleString()}</Volume>
        <Description data-tooltip-id={`infoicon-${unit}`}>
          {cardData?.description}
        </Description>
        {isVerifiedEstimatedFeatureEnabled && (
          <VerifiedEstimatedContainer>
            <VerifiedContainer>
              <StyledLabelText>Verified</StyledLabelText>
              <StyledH4>{`${verified}%`}</StyledH4>
            </VerifiedContainer>
            <EstimatedContainer>
              <StyledLabelText>Estimated</StyledLabelText>
              <StyledH4>{`${estimated}%`}</StyledH4>
            </EstimatedContainer>
          </VerifiedEstimatedContainer>
        )}
      </DataContainer>
      {unitWithTooltip && (
        <StyledTooltip
          id={`infoicon-${unit}`}
          place="top"
          noArrow
          content={tooltipContent}
          opacity={1}
        />
      )}
    </Container>
  );
}

export default ImpactCard;
